import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getArticleBySlug, articles } from '../data/articles/index';
import ReactMarkdown from 'react-markdown';

const ArticleDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = slug ? getArticleBySlug(slug) : undefined;
  
  // Get the 3 most recent articles (excluding current)
  const recentArticles = articles
    .filter(a => a.slug !== slug)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  useEffect(() => {
    // Redirect to 404 if article not found
    if (!article && slug) {
      navigate('/not-found', { replace: true });
    }
    
    // Scroll to top when article loads
    window.scrollTo(0, 0);
  }, [article, navigate, slug]);

  // Format the date nicely
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IE', options);
  };

  // Process article content to remove the first heading (h1)
  const processedContent = article?.content
    ? article.content.replace(/^# .+$/m, '') // Remove the first heading
    : '';

  if (!article) {
    return null;
  }

  return (
    <div className="pt-48 pb-40 md:pt-48 md:pb-48">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/articles"
          className="inline-flex items-center text-[#FFFFFFCC] hover:text-primary mb-10 transition-colors duration-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Articles
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#333333] px-3 py-1 rounded-full text-sm text-[#FFFFFFAA]">
              {article.category}
            </span>
            <span className="text-sm text-[#FFFFFFAA]">
              {formatDate(article.date)}
            </span>
            <span className="text-sm text-[#FFFFFFAA]">
              {article.readTime} min read
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            {article.title}
          </h1>
        </div>

        {/* Featured Image */}
        <div className="relative rounded-xl overflow-hidden mb-10 h-[300px] md:h-[400px]">
          <img 
            src={article.coverImage} 
            alt={article.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = '/images/article-placeholder.jpg';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-40" />
        </div>

        {/* Article Content with improved text layout */}
        <div className="mb-16">
          <div className="prose prose-lg prose-invert max-w-none 
            prose-headings:font-bold prose-headings:font-montserrat
            prose-h1:text-3xl prose-h1:mt-12 prose-h1:mb-6 
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5 
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
            prose-p:my-6 prose-p:text-[#FFFFFFEE] prose-p:leading-relaxed prose-p:text-lg
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-strong:text-white prose-strong:font-bold
            prose-li:my-3 prose-li:text-[#FFFFFFEE]
            prose-ul:my-6 prose-ul:pl-5
            prose-ol:my-6 prose-ol:pl-5
            prose-blockquote:border-l-4 prose-blockquote:border-primary/50 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-[#FFFFFFCC]
            prose-hr:border-[#333333] prose-hr:my-10
            prose-img:rounded-lg prose-img:my-8">
            <ReactMarkdown>
              {processedContent}
            </ReactMarkdown>
          </div>
        </div>

        {/* Recent Articles Section */}
        <div className="mt-16 pt-10 border-t border-[#333333]">
          <h2 className="text-2xl font-bold text-white mb-8">Recent Articles</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {recentArticles.map(recentArticle => (
              <Link 
                to={`/articles/${recentArticle.slug}`} 
                key={recentArticle.id}
                className="group"
                onClick={() => window.scrollTo(0, 0)}
              >
                <article className="bg-[#1A1A1A] rounded-xl overflow-hidden transition-transform duration-300 h-full flex flex-col hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-1">
                  {/* Image Container */}
                  <div className="relative h-40 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent opacity-70 z-10"/>
                    <img 
                      src={recentArticle.coverImage} 
                      alt={recentArticle.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.src = '/images/article-placeholder.jpg';
                      }}
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="flex flex-col flex-grow p-4">
                    {/* Category & Date */}
                    <div className="flex justify-between items-center mb-2 text-xs text-[#FFFFFFAA]">
                      <span className="bg-[#333333] px-2 py-1 rounded-full">{recentArticle.category}</span>
                      <span>{formatDate(recentArticle.date)}</span>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-primary transition-colors duration-200 line-clamp-2 overflow-hidden">
                      {recentArticle.title}
                    </h3>
                    
                    {/* Read Time */}
                    <div className="mt-auto pt-3 text-xs text-[#FFFFFFAA]">
                      <span>{recentArticle.readTime} min read</span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail; 